import { useState, useEffect } from 'react'
import { Header } from '../components/layout/Header'

const API_BASE = '/api'

interface Tag {
  id: string
  name: string
  color: string
  category: string
  usage_count: number
}

interface Label {
  id: string
  name: string
  description: string
  color: string
  items_count: number
}

interface AutoRule {
  id: string
  name: string
  description: string
  condition: any
  action: any
  enabled: boolean
  applied_count: number
}

export default function TagManagementPage() {
  const [tags, setTags] = useState<Tag[]>([])
  const [labels, setLabels] = useState<Label[]>([])
  const [autoRules, setAutoRules] = useState<AutoRule[]>([])
  const [statistics, setStatistics] = useState<any>(null)
  const [activeTab, setActiveTab] = useState<'tags' | 'labels' | 'rules'>('tags')
  const [selectedCategory, setSelectedCategory] = useState<string | null>(null)

  useEffect(() => {
    fetchData()
  }, [selectedCategory])

  const fetchData = async () => {
    const catParam = selectedCategory ? `?category=${selectedCategory}` : ''
    const [tagRes, lblRes, ruleRes, statsRes] = await Promise.all([
      fetch(API_BASE + '/tags' + catParam),
      fetch(API_BASE + '/tags/labels/all'),
      fetch(API_BASE + '/tags/auto-rules/all'),
      fetch(API_BASE + '/tags/statistics')
    ])
    if (tagRes.ok) setTags((await tagRes.json()).tags || [])
    if (lblRes.ok) setLabels((await lblRes.json()).labels || [])
    if (ruleRes.ok) setAutoRules((await ruleRes.json()).rules || [])
    if (statsRes.ok) setStatistics(await statsRes.json())
  }

  const toggleRule = async (id: string, enabled: boolean) => {
    await fetch(API_BASE + '/tags/auto-rules/' + id + '/toggle', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ enabled })
    })
    fetchData()
  }

  const categories = statistics?.categories ? Object.entries(statistics.categories) : []

  return (
    <div className="min-h-screen bg-gray-50">
      <Header />
      <main className="max-w-7xl mx-auto px-4 py-8">
        <h1 className="text-3xl font-bold mb-8">Tag & Label Management</h1>

        {statistics && (
          <div className="grid grid-cols-5 gap-4 mb-8">
            <div className="bg-white rounded-lg shadow p-4">
              <div className="text-sm text-gray-500">Total Tags</div>
              <div className="text-2xl font-bold">{statistics.total_tags}</div>
            </div>
            <div className="bg-white rounded-lg shadow p-4">
              <div className="text-sm text-gray-500">Labels</div>
              <div className="text-2xl font-bold">{statistics.total_labels}</div>
            </div>
            <div className="bg-white rounded-lg shadow p-4">
              <div className="text-sm text-gray-500">Total Usage</div>
              <div className="text-2xl font-bold">{statistics.total_usage?.toLocaleString()}</div>
            </div>
            <div className="bg-white rounded-lg shadow p-4">
              <div className="text-sm text-gray-500">Auto Rules</div>
              <div className="text-2xl font-bold">{statistics.auto_rules}</div>
            </div>
            <div className="bg-white rounded-lg shadow p-4">
              <div className="text-sm text-gray-500">Active Rules</div>
              <div className="text-2xl font-bold text-green-600">{statistics.active_rules}</div>
            </div>
          </div>
        )}

        <div className="flex gap-4 mb-6">
          <button onClick={() => setActiveTab('tags')} className={`px-4 py-2 rounded-lg ${activeTab === 'tags' ? 'bg-blue-600 text-white' : 'bg-white border'}`}>Tags</button>
          <button onClick={() => setActiveTab('labels')} className={`px-4 py-2 rounded-lg ${activeTab === 'labels' ? 'bg-blue-600 text-white' : 'bg-white border'}`}>Labels</button>
          <button onClick={() => setActiveTab('rules')} className={`px-4 py-2 rounded-lg ${activeTab === 'rules' ? 'bg-blue-600 text-white' : 'bg-white border'}`}>Auto Rules</button>
        </div>

        {activeTab === 'tags' && (
          <div className="grid grid-cols-4 gap-6">
            <div className="bg-white rounded-lg shadow p-4">
              <h3 className="font-semibold mb-4">Categories</h3>
              <div className="space-y-2">
                <button onClick={() => setSelectedCategory(null)} className={`w-full text-left px-3 py-2 rounded ${!selectedCategory ? 'bg-blue-100 text-blue-800' : 'hover:bg-gray-100'}`}>All</button>
                {categories.map(([cat, count]: [string, any]) => (
                  <button key={cat} onClick={() => setSelectedCategory(cat)} className={`w-full text-left px-3 py-2 rounded flex justify-between ${selectedCategory === cat ? 'bg-blue-100 text-blue-800' : 'hover:bg-gray-100'}`}>
                    <span className="capitalize">{cat}</span>
                    <span className="text-xs bg-gray-200 px-2 py-1 rounded">{count}</span>
                  </button>
                ))}
              </div>
            </div>
            <div className="col-span-3">
              <div className="bg-white rounded-lg shadow p-6">
                <div className="flex justify-between items-center mb-4">
                  <h3 className="font-semibold">Tags</h3>
                  <button className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700">+ Add Tag</button>
                </div>
                <div className="flex flex-wrap gap-3">
                  {tags.map(tag => (
                    <div key={tag.id} className="flex items-center gap-2 px-3 py-2 rounded-lg border" style={{ borderColor: tag.color }}>
                      <div className="w-3 h-3 rounded-full" style={{ backgroundColor: tag.color }}></div>
                      <span className="font-medium">{tag.name}</span>
                      <span className="text-xs text-gray-500">{tag.usage_count}</span>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </div>
        )}

        {activeTab === 'labels' && (
          <div className="grid grid-cols-3 gap-6">
            {labels.map(label => (
              <div key={label.id} className="bg-white rounded-lg shadow p-6" style={{ borderTop: `4px solid ${label.color}` }}>
                <div className="flex justify-between items-start mb-2">
                  <h3 className="font-semibold">{label.name}</h3>
                  <span className="text-sm text-gray-500">{label.items_count} items</span>
                </div>
                <p className="text-sm text-gray-600">{label.description}</p>
              </div>
            ))}
            <div className="bg-white rounded-lg shadow p-6 border-2 border-dashed border-gray-300 flex items-center justify-center cursor-pointer hover:border-blue-500">
              <span className="text-gray-500">+ Add Label</span>
            </div>
          </div>
        )}

        {activeTab === 'rules' && (
          <div className="bg-white rounded-lg shadow">
            <div className="px-6 py-4 border-b flex justify-between items-center">
              <h2 className="text-xl font-semibold">Auto-Tagging Rules</h2>
              <button className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700">+ Add Rule</button>
            </div>
            <div className="divide-y">
              {autoRules.map(rule => (
                <div key={rule.id} className="p-4">
                  <div className="flex justify-between items-start mb-2">
                    <div>
                      <div className="font-semibold">{rule.name}</div>
                      <div className="text-sm text-gray-500">{rule.description}</div>
                    </div>
                    <div className="flex items-center gap-3">
                      <span className="text-sm text-gray-500">{rule.applied_count} applied</span>
                      <button
                        onClick={() => toggleRule(rule.id, !rule.enabled)}
                        className={`px-3 py-1 rounded text-sm ${rule.enabled ? 'bg-green-100 text-green-800' : 'bg-gray-100 text-gray-800'}`}
                      >
                        {rule.enabled ? 'Enabled' : 'Disabled'}
                      </button>
                    </div>
                  </div>
                  <div className="flex gap-4 text-xs">
                    <span className="px-2 py-1 bg-gray-100 rounded">Condition: {rule.condition.field} {rule.condition.operator} {rule.condition.value}</span>
                    <span className="px-2 py-1 bg-blue-100 text-blue-800 rounded">Action: {rule.action.type}</span>
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}
      </main>
    </div>
  )
}
